import React, { useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Search, X, SlidersHorizontal } from 'lucide-react';
import { PRODUCTS, COLLECTIONS } from '../mock';
import { getBrandBySlug } from '../data/brands';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../components/ui/sheet';

// Derive a clean, shoppable part category from the product name. Most Paktechz
// & Sooqoo parts are tagged generically ("Vehicle Parts & Accessories") in
// WooCommerce, so the name is the reliable signal.
const PART_BUCKETS = [
  ['Full Body Kit', /body\s*kit|full kit/i],
  ['Front Lip', /front lip/i],
  ['Front Splitter', /splitter/i],
  ['Rear Diffuser', /diffuser/i],
  ['Spoiler & Wing', /spoiler|wing/i],
  ['Side Skirts', /side skirt|\bskirt/i],
  ['Hood', /hood|bonnet/i],
  ['Canards', /canard/i],
  ['Fenders', /fender/i],
  ['Mirror Caps', /mirror/i],
  ['Grille', /grille|grill\b/i],
  ['Front Bumper', /front bumper/i],
  ['Rear Bumper', /rear bumper/i],
  ['Rear Lip', /rear lip/i],
];
const inferPartType = (name = '') => {
  for (const [label, re] of PART_BUCKETS) if (re.test(name)) return label;
  return 'Other';
};

const SORTS = {
  featured: 'Featured',
  'price-asc': 'Price: Low to High',
  'price-desc': 'Price: High to Low',
};

const FilterSection = ({ title, children }) => (
  <div className="border-b border-neutral-200 py-6 first:pt-0">
    <h3 className="font-display tracking-[0.22em] text-[11px] text-neutral-900 font-semibold mb-4">
      {title}
    </h3>
    {children}
  </div>
);

const CheckRow = ({ id, label, count, checked, onChange, testid }) => (
  <label
    htmlFor={id}
    className="flex items-center gap-3 py-1.5 cursor-pointer group select-none"
    data-testid={testid}
  >
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#FF6B00] data-[state=checked]:border-[#FF6B00] border-neutral-400 rounded-none"
    />
    <span className="text-sm text-neutral-700 group-hover:text-neutral-900 transition-colors flex-1 min-w-0 truncate">
      {label}
    </span>
    <span className="text-[11px] text-neutral-400 tabular-nums">{count}</span>
  </label>
);

const BrandHub = () => {
  const { brandSlug } = useParams();
  const brand = getBrandBySlug(brandSlug);

  const [search, setSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [selectedModels, setSelectedModels] = useState(() => new Set());
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [sort, setSort] = useState('featured');
  const [mobileOpen, setMobileOpen] = useState(false);

  // All products for this brand (with derived part type + model name).
  const items = useMemo(() => {
    if (!brand) return [];
    const brandNames = brand.brandNames || [brand.name];
    const collectionIds = brand.collectionIds || [];
    return PRODUCTS
      .filter((p) => brandNames.includes(p.brand) || collectionIds.includes(p.collectionId))
      .map((p) => {
        const col = COLLECTIONS.find((c) => c.id === p.collectionId);
        const isWheel = p.collectionId === 'fortune-forged-wheels' || p.brand === 'Fortune Black Label';
        return { ...p, _model: col?.name || 'Other', _part: isWheel ? 'Forged Wheels' : inferPartType(p.name) };
      });
  }, [brand]);

  const modelFacets = useMemo(() => {
    const m = new Map();
    items.forEach((p) => {
      const key = p.collectionId;
      if (!m.has(key)) m.set(key, { id: key, name: p._model, count: 0 });
      m.get(key).count += 1;
    });
    return [...m.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [items]);

  const catFacets = useMemo(() => {
    const m = new Map();
    items.forEach((p) => m.set(p._part, (m.get(p._part) || 0) + 1));
    return [...m.entries()].map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [items]);

  const onSaleCount = useMemo(
    () => items.filter((p) => p.compareAtPrice && p.compareAtPrice > p.price).length,
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = items.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (selectedModels.size && !selectedModels.has(p.collectionId)) return false;
      if (selectedCats.size && !selectedCats.has(p._part)) return false;
      if (onSaleOnly && !(p.compareAtPrice && p.compareAtPrice > p.price)) return false;
      return true;
    });
    if (sort === 'price-asc') out = [...out].sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') out = [...out].sort((a, b) => b.price - a.price);
    return out;
  }, [items, search, selectedModels, selectedCats, onSaleOnly, sort]);

  // Vehicle collections carried by this brand — SEO cross-links.
  const vehicles = useMemo(() => {
    const ids = new Set(items.map((p) => p.collectionId));
    return COLLECTIONS.filter((c) => ids.has(c.id));
  }, [items]);

  if (!brand) return <Navigate to="/shop" replace />;

  const enableFilters = items.length > 4;
  const hasModelFilter = modelFacets.length > 1;
  const activeCount =
    selectedModels.size + selectedCats.size + (onSaleOnly ? 1 : 0) + (search ? 1 : 0);

  const toggleSet = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleModel = toggleSet(setSelectedModels);
  const toggleCat = toggleSet(setSelectedCats);

  const clearAll = () => {
    setSearch('');
    setModelSearch('');
    setSelectedModels(new Set());
    setSelectedCats(new Set());
    setOnSaleOnly(false);
  };

  const visibleModelFacets = modelFacets.filter((m) =>
    !modelSearch.trim() || m.name.toLowerCase().includes(modelSearch.trim().toLowerCase()));

  const FilterPanel = () => (
    <div data-testid="brandhub-filter-panel">
      {hasModelFilter && (
        <FilterSection title="VEHICLE MODEL">
          {modelFacets.length > 10 && (
            <div className="relative mb-3">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                placeholder="Find a model…"
                className="pl-8 h-9 text-sm rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-[#FF6B00]"
                data-testid="brandhub-model-search"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto pr-1 -mr-1">
            {visibleModelFacets.map((m) => (
              <CheckRow
                key={m.id}
                id={`m-${m.id}`}
                label={m.name}
                count={m.count}
                checked={selectedModels.has(m.id)}
                onChange={() => toggleModel(m.id)}
                testid={`filter-model-${m.id}`}
              />
            ))}
            {visibleModelFacets.length === 0 && (
              <p className="text-sm text-neutral-400 py-2">No models match.</p>
            )}
          </div>
        </FilterSection>
      )}

      {catFacets.length > 1 && (
        <FilterSection title="PART CATEGORY">
          <div className="max-h-72 overflow-y-auto pr-1 -mr-1">
            {catFacets.map((c) => (
              <CheckRow
                key={c.name}
                id={`c-${c.name}`}
                label={c.name}
                count={c.count}
                checked={selectedCats.has(c.name)}
                onChange={() => toggleCat(c.name)}
                testid={`filter-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {onSaleCount > 0 && (
        <FilterSection title="PRICE">
          <label className="flex items-center justify-between gap-3 cursor-pointer" htmlFor="on-sale">
            <span className="text-sm text-neutral-700">On sale only</span>
            <Switch
              id="on-sale"
              checked={onSaleOnly}
              onCheckedChange={setOnSaleOnly}
              className="data-[state=checked]:bg-[#FF6B00]"
              data-testid="filter-on-sale"
            />
          </label>
          <p className="text-[11px] text-neutral-400 mt-2">{onSaleCount} on sale</p>
        </FilterSection>
      )}
    </div>
  );

  return (
    <div className="bg-white">
      {/* ===== HERO (dark, with logo lockup) ===== */}
      <section className="relative bg-[#0a0a0a] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{ background: 'radial-gradient(1200px 400px at 15% -10%, rgba(255,107,0,0.18), transparent 60%)' }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <nav className="text-[11px] font-display tracking-[0.22em] text-neutral-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">HOME</Link>
            <span className="mx-2 text-neutral-600">/</span>
            <Link to="/shop" className="hover:text-white transition-colors">BRANDS</Link>
            <span className="mx-2 text-neutral-600">/</span>
            <span className="text-[#FF6B00]">{brand.name.toUpperCase()}</span>
          </nav>

          <div className="font-display tracking-[0.3em] text-[12px] text-[#FF6B00] font-semibold mb-5">
            {brand.kicker.toUpperCase()}
          </div>

          {/* Logo lockup (falls back to wordmark) */}
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={`${brand.name} logo`}
              className="h-14 md:h-[68px] w-auto max-w-[300px] md:max-w-[380px] object-contain object-left mb-7"
              data-testid="brandhub-logo"
            />
          ) : (
            <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tight mb-7" data-testid="brandhub-wordmark">
              {brand.name}
            </h1>
          )}

          <p className="text-neutral-300 leading-relaxed max-w-3xl">{brand.intro[0]}</p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
            {brand.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-neutral-200">
                <span className="mt-[6px] h-[6px] w-[6px] bg-[#FF6B00] flex-shrink-0" />
                {h}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex items-center gap-4">
            <span className="font-display tracking-[0.2em] text-sm text-white">
              {items.length} {items.length === 1 ? 'PART' : 'PARTS'} AVAILABLE
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
        </div>
      </section>

      {/* ===== CATALOG (light) ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className={enableFilters ? 'lg:grid lg:grid-cols-[260px_1fr] lg:gap-12' : ''}>
          {/* Desktop sidebar */}
          {enableFilters && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] font-semibold">
                    REFINE
                  </h2>
                  {activeCount > 0 && (
                    <button
                      onClick={clearAll}
                      className="text-[11px] text-neutral-500 hover:text-[#FF6B00] transition-colors"
                      data-testid="brandhub-clear-desktop"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <FilterPanel />
              </div>
            </aside>
          )}

          {/* Main column */}
          <div className="min-w-0">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-5 border-b border-neutral-200">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${brand.name} parts…`}
                  className="pl-9 h-11 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-[#FF6B00]"
                  data-testid="brandhub-search"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-900"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                {enableFilters && (
                  <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        className="lg:hidden h-11 rounded-none border-neutral-300 text-neutral-900 font-display tracking-[0.15em] text-xs gap-2"
                        data-testid="brandhub-mobile-filter-trigger"
                      >
                        <SlidersHorizontal size={15} />
                        FILTERS
                        {activeCount > 0 && (
                          <span className="ml-1 bg-[#FF6B00] text-white text-[10px] px-1.5 py-0.5">{activeCount}</span>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-white">
                      <SheetHeader className="mb-2">
                        <SheetTitle className="font-display tracking-[0.22em] text-sm">
                          REFINE {brand.name.toUpperCase()}
                        </SheetTitle>
                      </SheetHeader>
                      <FilterPanel />
                      <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-4">
                        <Button
                          variant="outline"
                          onClick={clearAll}
                          className="flex-1 rounded-none border-neutral-300 text-neutral-900 font-display tracking-[0.15em] text-xs"
                        >
                          CLEAR
                        </Button>
                        <Button
                          onClick={() => setMobileOpen(false)}
                          className="flex-1 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs"
                          data-testid="brandhub-mobile-apply"
                        >
                          SHOW {filtered.length}
                        </Button>
                      </div>
                    </SheetContent>
                  </Sheet>
                )}

                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger
                    className="h-11 w-[190px] rounded-none border-neutral-300 focus:ring-0 font-display tracking-[0.12em] text-xs"
                    data-testid="brandhub-sort"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(SORTS).map(([v, label]) => (
                      <SelectItem key={v} value={v} className="text-sm" data-testid={`sort-${v}`}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active chips + count */}
            <div className="flex items-center flex-wrap gap-2 py-4">
              <span className="text-sm text-neutral-500 mr-1" aria-live="polite" data-testid="brandhub-result-count">
                Showing <span className="text-neutral-900 font-medium">{filtered.length}</span> of {items.length} parts
              </span>
              {[...selectedModels].map((id) => {
                const f = modelFacets.find((m) => m.id === id);
                return f ? (
                  <Badge key={`m${id}`} variant="secondary" className="rounded-none bg-neutral-100 text-neutral-700 gap-1 font-normal">
                    {f.name}
                    <button onClick={() => toggleModel(id)} aria-label={`Remove ${f.name}`}><X size={12} /></button>
                  </Badge>
                ) : null;
              })}
              {[...selectedCats].map((c) => (
                <Badge key={`c${c}`} variant="secondary" className="rounded-none bg-neutral-100 text-neutral-700 gap-1 font-normal">
                  {c}
                  <button onClick={() => toggleCat(c)} aria-label={`Remove ${c}`}><X size={12} /></button>
                </Badge>
              ))}
              {onSaleOnly && (
                <Badge variant="secondary" className="rounded-none bg-neutral-100 text-neutral-700 gap-1 font-normal">
                  On sale
                  <button onClick={() => setOnSaleOnly(false)} aria-label="Remove on sale"><X size={12} /></button>
                </Badge>
              )}
              {activeCount > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs text-[#FF6B00] hover:underline ml-1"
                  data-testid="brandhub-clear-chips"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Grid / empty state */}
            {filtered.length === 0 ? (
              <div className="py-24 text-center border border-neutral-200 bg-neutral-50" data-testid="brandhub-empty">
                <p className="font-display tracking-[0.15em] text-neutral-500 mb-4">NO PARTS MATCH YOUR FILTERS</p>
                <Button onClick={clearAll} className="rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs">
                  CLEAR FILTERS
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-10" data-testid="brand-product-grid">
                {filtered.map((p) => {
                  const onSale = p.compareAtPrice && p.compareAtPrice > p.price;
                  const hoverImg = p.images[1] || p.images[0];
                  return (
                    <Link key={p.id} to={`/product/${p.slug}`} className="group block" data-testid={`brand-product-${p.id}`}>
                      <div className="relative aspect-square bg-neutral-100 overflow-hidden mb-4">
                        <img
                          src={p.images[0]}
                          alt={`${p.brand} ${p.name}`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                        />
                        {hoverImg !== p.images[0] && (
                          <img
                            src={hoverImg}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          />
                        )}
                        <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#FF6B00] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                        <span className="absolute top-3 left-3 bg-white/95 text-neutral-900 text-[9px] font-display tracking-[0.22em] px-2 py-1 backdrop-blur">
                          MADE TO ORDER
                        </span>
                        {onSale && (
                          <span className="absolute top-3 right-3 bg-[#FF6B00] text-white text-[10px] font-display tracking-[0.22em] px-2 py-1">
                            SALE
                          </span>
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] font-semibold mb-1.5">{p.brand.toUpperCase()}</div>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-display text-2xl font-bold tracking-tight text-neutral-900 leading-none">${p.price.toLocaleString()}</span>
                            <span className="text-neutral-400 text-[11px]">USD</span>
                            {onSale && (
                              <span className="text-neutral-400 line-through text-sm">${p.compareAtPrice.toLocaleString()}</span>
                            )}
                          </div>
                          <h3 className="font-display text-base font-normal tracking-wide text-neutral-500 leading-snug mt-1.5 group-hover:text-[#FF6B00] transition-colors truncate">{p.name}</h3>
                        </div>
                        <ArrowRight size={18} className="text-neutral-400 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all mt-1 flex-shrink-0" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== BROWSE BY VEHICLE (SEO cross-links) ===== */}
      {vehicles.length > 0 && (
        <section className="border-t border-neutral-200 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
            <h2 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] font-semibold mb-4">
              SHOP {brand.name.toUpperCase()} BY VEHICLE
            </h2>
            <div className="flex flex-wrap gap-2">
              {vehicles.map((c) => (
                <Link
                  key={c.id}
                  to={`/collections/${c.id}`}
                  className="bg-white border border-neutral-300 hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors px-3 py-1.5 text-sm text-neutral-700 whitespace-nowrap"
                  data-testid={`brandhub-vehicle-link-${c.id}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA ===== */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center">
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-3">
          Not sure which {brand.name} parts fit your build?
        </h2>
        <p className="text-neutral-600 max-w-2xl mx-auto mb-6">
          Our team will confirm fitment for your exact chassis and put together a made-to-order quote.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#FF6B00] transition-colors px-8 py-4 font-display tracking-[0.22em] text-sm"
        >
          GET A QUOTE <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
};

export default BrandHub;
